import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Package, Plus, Edit2, Trash2, Save, X, 
  Search, Image, DollarSign, Settings 
} from 'lucide-react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import Navbar from '@/components/layout/Navbar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : '/api';

const Admin = () => {
  const { t, getLocalizedField } = useLanguage();
  const { user, isAdmin, token } = useAuth();
  const navigate = useNavigate();

  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [editingProduct, setEditingProduct] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const emptyProduct = {
    name_en: '',
    name_es: '',
    description_en: '',
    description_es: '',
    price: 0,
    original_price: null,
    category_id: '',
    image: '',
    images: [],
    brand: '',
    unit: '',
    stock: 100,
    is_eco: false,
    is_featured: false,
    is_on_sale: false,
    origin: '',
    benefits_en: [],
    benefits_es: [],
  };

  const [formData, setFormData] = useState(emptyProduct);

  useEffect(() => {
    if (!isAdmin) {
      navigate('/');
      return;
    }
    fetchData();
  }, [isAdmin, navigate]);

  const fetchData = async () => {
    try {
      const [productsRes, categoriesRes] = await Promise.all([
        axios.get(`${API}/products`),
        axios.get(`${API}/categories`),
      ]);
      setProducts(productsRes.data);
      setCategories(categoriesRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveProduct = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      if (editingProduct) {
        await axios.put(`${API}/admin/products/${editingProduct.id}`, formData, { headers });
        toast.success('Product updated successfully');
      } else {
        await axios.post(`${API}/admin/products`, formData, { headers });
        toast.success('Product created successfully');
      }
      setIsDialogOpen(false);
      setEditingProduct(null);
      setFormData(emptyProduct);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error saving product');
    }
  };

  const handleDeleteProduct = async (productId) => {
    if (!window.confirm('Are you sure you want to delete this product?')) return;
    try {
      await axios.delete(`${API}/admin/products/${productId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Product deleted successfully');
      fetchData();
    } catch (error) {
      toast.error('Error deleting product');
    }
  };

  const openEditDialog = (product) => {
    setEditingProduct(product);
    setFormData({
      ...product,
      original_price: product.original_price || null,
    });
    setIsDialogOpen(true);
  };

  const openCreateDialog = () => {
    setEditingProduct(null);
    setFormData(emptyProduct);
    setIsDialogOpen(true);
  };

  const filteredProducts = products.filter(p =>
    p.name_en.toLowerCase().includes(searchQuery.toLowerCase()) ||
    p.name_es.toLowerCase().includes(searchQuery.toLowerCase()) ||
    p.brand.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (!isAdmin) {
    return null;
  }

  return (
    <div className="min-h-screen bg-background" data-testid="admin-page">
      <Navbar />
      
      <main className="pt-24 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8"
          >
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-2xl gradient-primary flex items-center justify-center">
                <Settings className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-slate-900">{t('admin.title')}</h1>
                <p className="text-slate-500">Manage your products</p>
              </div>
            </div>

            <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
              <DialogTrigger asChild>
                <Button onClick={openCreateDialog} className="btn-primary" data-testid="add-product-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  {t('admin.addProduct')}
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>
                    {editingProduct ? t('admin.editProduct') : t('admin.addProduct')}
                  </DialogTitle>
                </DialogHeader>
                <div className="space-y-4 mt-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium">Name (English)</label>
                      <Input
                        value={formData.name_en}
                        onChange={(e) => setFormData(prev => ({ ...prev, name_en: e.target.value }))}
                        placeholder="Product name"
                        data-testid="product-name-en"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium">Name (Spanish)</label>
                      <Input
                        value={formData.name_es}
                        onChange={(e) => setFormData(prev => ({ ...prev, name_es: e.target.value }))}
                        placeholder="Nombre del producto"
                        data-testid="product-name-es"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium">Description (English)</label>
                      <Textarea
                        value={formData.description_en}
                        onChange={(e) => setFormData(prev => ({ ...prev, description_en: e.target.value }))}
                        rows={2}
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium">Description (Spanish)</label>
                      <Textarea
                        value={formData.description_es}
                        onChange={(e) => setFormData(prev => ({ ...prev, description_es: e.target.value }))}
                        rows={2}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="text-sm font-medium">Price</label>
                      <Input
                        type="number"
                        step="0.01"
                        value={formData.price}
                        onChange={(e) => setFormData(prev => ({ ...prev, price: parseFloat(e.target.value) || 0 }))}
                        data-testid="product-price"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium">Original Price</label>
                      <Input
                        type="number"
                        step="0.01"
                        value={formData.original_price || ''}
                        onChange={(e) => setFormData(prev => ({ 
                          ...prev, 
                          original_price: e.target.value ? parseFloat(e.target.value) : null 
                        }))}
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium">Stock</label>
                      <Input
                        type="number"
                        value={formData.stock}
                        onChange={(e) => setFormData(prev => ({ ...prev, stock: parseInt(e.target.value) || 0 }))}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium">Category</label>
                      <Select
                        value={formData.category_id}
                        onValueChange={(value) => setFormData(prev => ({ ...prev, category_id: value }))}
                      >
                        <SelectTrigger data-testid="product-category">
                          <SelectValue placeholder="Select category" />
                        </SelectTrigger>
                        <SelectContent>
                          {categories.map((cat) => (
                            <SelectItem key={cat.id} value={cat.id}>
                              {getLocalizedField(cat, 'name')}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="text-sm font-medium">Brand</label>
                      <Input
                        value={formData.brand}
                        onChange={(e) => setFormData(prev => ({ ...prev, brand: e.target.value }))}
                        placeholder="Brand name"
                        data-testid="product-brand"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium">Unit</label>
                      <Input
                        value={formData.unit}
                        onChange={(e) => setFormData(prev => ({ ...prev, unit: e.target.value }))}
                        placeholder="e.g., 1 kg, 500g"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium">Origin</label>
                      <Input
                        value={formData.origin || ''}
                        onChange={(e) => setFormData(prev => ({ ...prev, origin: e.target.value }))}
                        placeholder="Country of origin"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="text-sm font-medium">Image URL</label>
                    <Input
                      value={formData.image}
                      onChange={(e) => setFormData(prev => ({ ...prev, image: e.target.value }))}
                      placeholder="https://..."
                      data-testid="product-image"
                    />
                    {formData.image && (
                      <img src={formData.image} alt="Preview" className="mt-2 w-24 h-24 object-cover rounded-lg" />
                    )}
                  </div>

                  <div className="flex flex-wrap gap-4">
                    <label className="flex items-center gap-2">
                      <Checkbox
                        checked={formData.is_eco}
                        onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_eco: checked }))}
                      />
                      <span className="text-sm">Eco-friendly</span>
                    </label>
                    <label className="flex items-center gap-2">
                      <Checkbox
                        checked={formData.is_featured}
                        onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_featured: checked }))}
                      />
                      <span className="text-sm">Featured</span>
                    </label>
                    <label className="flex items-center gap-2">
                      <Checkbox
                        checked={formData.is_on_sale}
                        onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_on_sale: checked }))}
                      />
                      <span className="text-sm">On Sale</span>
                    </label>
                  </div>

                  <div className="flex justify-end gap-3 pt-4">
                    <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
                      <X className="w-4 h-4 mr-2" />
                      {t('admin.cancel')}
                    </Button>
                    <Button onClick={handleSaveProduct} className="btn-primary" data-testid="save-product-btn">
                      <Save className="w-4 h-4 mr-2" />
                      {t('admin.save')}
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </motion.div>

          {/* Search */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6"
          >
            <div className="relative w-full md:w-80">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search products..."
                className="pl-10"
                data-testid="admin-search"
              />
            </div>
          </motion.div>

          {/* Products Table */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="card-glass overflow-hidden"
          >
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-slate-600">Product</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-slate-600">Category</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-slate-600">Price</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-slate-600">Stock</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-slate-600">Status</th>
                    <th className="px-4 py-3 text-right text-sm font-semibold text-slate-600">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filteredProducts.map((product) => {
                    const category = categories.find(c => c.id === product.category_id);
                    return (
                      <tr key={product.id} className="hover:bg-slate-50/50" data-testid={`product-row-${product.id}`}>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            <img
                              src={product.image}
                              alt={product.name_en}
                              className="w-12 h-12 rounded-lg object-cover"
                            />
                            <div>
                              <p className="font-medium text-slate-900">{product.name_en}</p>
                              <p className="text-sm text-slate-500">{product.brand}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-slate-600">
                          {category ? getLocalizedField(category, 'name') : '-'}
                        </td>
                        <td className="px-4 py-3">
                          <span className="font-semibold text-primary">${product.price.toFixed(2)}</span>
                          {product.original_price && (
                            <span className="text-sm text-slate-400 line-through ml-2">
                              ${product.original_price.toFixed(2)}
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-slate-600">{product.stock}</td>
                        <td className="px-4 py-3">
                          <div className="flex flex-wrap gap-1">
                            {product.is_featured && (
                              <span className="px-2 py-0.5 text-xs rounded-full bg-primary/10 text-primary">Featured</span>
                            )}
                            {product.is_on_sale && (
                              <span className="px-2 py-0.5 text-xs rounded-full bg-secondary/10 text-secondary">Sale</span>
                            )}
                            {product.is_eco && (
                              <span className="px-2 py-0.5 text-xs rounded-full bg-green-100 text-green-600">Eco</span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-end gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openEditDialog(product)}
                              data-testid={`edit-product-${product.id}`}
                            >
                              <Edit2 className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteProduct(product.id)}
                              className="text-red-500 hover:text-red-600 hover:bg-red-50"
                              data-testid={`delete-product-${product.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </motion.div>
        </div>
      </main>
    </div>
  );
};

export default Admin;
